import React from 'react';
import { Row, List, Col, Space, Divider } from 'antd';
import styled from 'styled-components';
import Link from 'next/link';
import NImage from 'next/image';
import Image from 'components/Image';
import { Title, Text } from 'components/ArticleCard';
import PremiumImage from 'public/static/images/premium.svg';

const BranchTitle = styled.h5`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
`;

interface Bolded {
    $bold?: boolean;
}

const BranchLink = styled(Link)<Bolded>`
    font-size: 14px;
    font-weight: ${({ $bold }) => ($bold ? 600 : 300)};
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: 0.14px;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.greyishBrownTwo};
`;

const AddressText = styled.p`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.35;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
`;

const ListItem = styled(List.Item)`
    border-bottom: 1px solid #dcdcdc !important;
`;

const CompanyDescription = styled(Text)`
    -webkit-line-clamp: 5;
`;

interface GroupedByBranchs {
    [key: string]: {
        title: string;
        seoSlug: string;
        items: [
            {
                title: string;
                seoSlug: string;
            }
        ];
    };
}

const groupByParentBranch = (branches: Branch[]): GroupedByBranchs => {
    return branches.reduce((r, a) => {
        if (a.lvl === 1) {
            r[a.seoSlug] = {
                title: a.title,
                seoSlug: a.seoSlug,
                items: []
            };
        } else {
            r[a.parent.seoSlug] = {
                title: a.parent.title,
                seoSlug: a.parent.seoSlug,
                items: [
                    ...(r[a.parent.seoSlug] || { items: [] }).items,
                    { title: a.title, seoSlug: a.seoSlug }
                ]
            };
        }
        return r;
    }, {});
};

interface Props {
    item: Company;
}

const Item = ({ item }: Props): React.JSX.Element => (
    <ListItem key={item.seoSlug}>
        <Col span={24}>
            {item.premium && (
                <div style={{ position: 'absolute', top: 0, right: 0 }}>
                    <NImage src={PremiumImage} width={120} height={22} alt="patronat" />
                </div>
            )}
            <Row gutter={16} align="middle">
                <Col xs={10} md={8}>
                    <Link href={`/katalog-firm/${item.seoSlug}`}>
                        <Image
                            type="logo"
                            height={270}
                            width={270}
                            alt={item.name}
                            src={item.logo}
                            style={{ maxWidth: '100%', objectFit: 'contain' }}
                        />
                    </Link>
                </Col>
                <Col xs={14} md={16}>
                    <Title>
                        <Link href={`/katalog-firm/${item.seoSlug}`}>{item.name}</Link>
                    </Title>
                    {item.addresses && item.addresses.length > 0 && (
                        <AddressText>Lokalizacja: {item.addresses[0].city}</AddressText>
                    )}
                    <CompanyDescription>
                        <Link href={`/katalog-firm/${item.seoSlug}`}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: item.about
                                }}
                            />
                        </Link>
                    </CompanyDescription>
                </Col>
            </Row>
            <Row
                style={{
                    background: '#f4f4f4',
                    margin: '25px 0',
                    padding: 20
                }}
            >
                <Col span={24}>
                    <BranchTitle>Branże powiązane z firmą:</BranchTitle>
                </Col>

                {Object.values(groupByParentBranch(item.branches)).map((category) => {
                    return (
                        <Col span={24} key={category.seoSlug}>
                            <Space wrap size={[4, 2]}>
                                <div>
                                    <BranchLink href={`?branch=${category.seoSlug}`} $bold>
                                        {category.title}
                                    </BranchLink>
                                    {category.items.length > 0 && `: `}
                                </div>

                                <Space
                                    size={[8, 1]}
                                    wrap
                                    split={
                                        <Divider
                                            type="vertical"
                                            style={{
                                                borderLeft: '1px solid black',
                                                margin: '0 1px'
                                            }}
                                        />
                                    }
                                >
                                    {category.items.length > 0 &&
                                        category.items.map((branch) => (
                                            <BranchLink
                                                key={branch.seoSlug}
                                                href={`?branch=${branch.seoSlug}`}
                                                passHref
                                            >
                                                {branch.title}
                                            </BranchLink>
                                        ))}
                                </Space>
                            </Space>
                        </Col>
                    );
                })}
            </Row>
        </Col>
    </ListItem>
);

export default Item;
